import React from 'react'
import { Box, Stack, Typography, IconButton } from '@mui/material'
import { motion, useReducedMotion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp,
  FaFacebookF,
  FaXTwitter,
  FaTiktok,
  FaSnapchat,
} from 'react-icons/fa6'

import footerBg from '../Images/Footer.jpg'

// خريطة تربط كل مفتاح رابط بالمسار الفعلي بتاعه في الراوتر
const routeMap = {
  home: '/',
  projects: '/projects',
  Blogs: '/blogs',
  about: '/about',
  contact: '/contact',
}

export const Footer = () => {
  const { t, i18n } = useTranslation()
  const isArabic = (i18n.language || 'ar').startsWith('ar')
  const prefersReducedMotion = useReducedMotion()

  const bg = '#2B2B2B'
  const maroon = '#7A2E3A'
  const ivory = '#D9D6CF'
  const ivoryMuted = 'rgba(217,214,207,0.65)'
  const hairline = 'rgba(217,214,207,0.14)'
  const contactBg = '#141312'

  const headingFont = isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif"
  const bodyFont = isArabic ? "'IBM Plex Sans Arabic', sans-serif" : "'Manrope', sans-serif"

  // منطقة التراكب - لازم تكون أصغر من أو تساوي pb بتاعة الكونتاكت (200px/300px)
  const OVERLAP = { xs: 160, md: 260 }

  const navLinks = [
    { key: 'home', label: t('nav.home') },
    { key: 'projects', label: t('nav.projects') },
    { key: 'Blogs', label: t('nav.Blogs') },
    { key: 'about', label: t('nav.about') },
    { key: 'contact', label: t('nav.contact') },
  ]

  const legalLinks = [
    { key: 'privacy', label: t('footer.privacy', isArabic ? 'سياسة الخصوصية' : 'Privacy Policy'), to: '/privacy-policy' },
    { key: 'terms', label: t('footer.terms', isArabic ? 'الشروط والأحكام' : 'Terms & Conditions'), to: '/terms-conditions' },
  ]

  const socialLinks = [
    { key: 'instagram', icon: <FaInstagram size={16} />, href: 'https://www.instagram.com/lyorauraproperties/' },
    { key: 'facebook', icon: <FaFacebookF size={16} />, href: 'https://www.facebook.com/profile.php?id=61593902888647' },
    // { key: 'twitter', icon: <FaXTwitter size={16} />, href: 'https://twitter.com' },
    { key: 'tiktok', icon: <FaTiktok size={16} />, href: 'https://www.tiktok.com/@lyoraura_properties' },
    // { key: 'snapchat', icon: <FaSnapchat size={16} />, href: 'https://snapchat.com' },
    // { key: 'linkedin', icon: <FaLinkedinIn size={16} />, href: 'https://linkedin.com' },
    { key: 'whatsapp', icon: <FaWhatsapp size={16} />, href: 'https://wa.me/971585535444' },
  ]

  const linkHoverSx = {
    fontFamily: bodyFont,
    color: ivoryMuted,
    fontSize: '0.92rem',
    textDecoration: 'none',
    display: 'inline-block',
    width: 'fit-content',
    borderBottom: '1px solid transparent',
    transition: 'color 0.2s ease, border-color 0.2s ease',
    cursor: 'pointer',
    '&:hover': { color: ivory, borderBottomColor: maroon },
  }

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        bgcolor: bg,
        // مسحوب لفوق بنفس ارتفاع منطقة التراكب بالظبط (بالـ px مباشرة، من غير لبس وحدات)
        // mt: { xs: '-160px', md: '-260px' },
        // زودنا الـ padding من فوق بنفس ارتفاع التراكب + مسافة تنفس طبيعية بعده
        pt: { xs: '80px', md: '124px' }, // 160+48 / 260+64
        zIndex: 3,
        overflow: 'hidden',
      }}
    >
      {/* صورة الخلفية - خافتة جدًا، مجرد ملمس مش عنصر أساسي */}
      <Box
        component="img"
        src={footerBg}
        alt=""
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: 'grayscale(1) contrast(1.1) brightness(0.35)',
          opacity: 0.2,
          zIndex: 0,
        }}
      />

      {/* الستارة - بلون الكونتاكت، بتختفي تدريجيًا بمنحنى ناعم (rgba بدل هيكس)
          وبتكشف خلفية الفوتر الحقيقية بالتدريج */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: OVERLAP,
          pointerEvents: 'none',
          zIndex: 0,
          background: `linear-gradient(180deg,
            ${contactBg} 0%,
            rgba(20,19,18,0.85) 15%,
            rgba(20,19,18,0.55) 35%,
            rgba(20,19,18,0.25) 60%,
            rgba(20,19,18,0.08) 80%,
            rgba(20,19,18,0) 100%)`,
        }}
      />

      <Box
        component={motion.div}
        initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        sx={{ position: 'relative', zIndex: 1, maxWidth: 1300, mx: 'auto', px: { xs: 3, md: 6 } }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 6, md: 8 }}
          justifyContent="space-between"
        >
          {/* عمود البراند */}
          <Box sx={{ maxWidth: 340 }}>
            <Typography
              component={Link}
              to="/"
              sx={{
                fontFamily: headingFont,
                letterSpacing: 1,
                color: ivory,
                fontWeight: 600,
                fontSize: '1.4rem',
                mb: 2,
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              Lyor Aura
            </Typography>
            <Typography
              sx={{
                fontFamily: bodyFont,
                color: ivoryMuted,
                fontSize: '0.9rem',
                lineHeight: 1.9,
                mb: 3,
              }}
            >
              {t(
                'footer.tagline',
                isArabic
                  ? 'شريكك في الاستثمار العقاري بأرقى مواقع دبي، بخبرة وشفافية في كل خطوة.'
                  : "Your partner in real estate investment across Dubai's finest locations."
              )}
            </Typography>

            <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.key}
                  component="a"
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.key}
                  sx={{
                    width: 36,
                    height: 36,
                    color: ivoryMuted,
                    border: `1px solid ${hairline}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: ivory,
                      bgcolor: maroon,
                      borderColor: maroon,
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Stack>
          </Box>

          {/* عمود الروابط السريعة - دلوقتي بتنقل فعليًا لمسارات الراوتر */}
          <Box sx={{ minWidth: 140 }}>
            <Typography
              sx={{
                fontFamily: bodyFont,
                color: ivory,
                fontSize: '0.85rem',
                fontWeight: 600,
                mb: 2.5,
              }}
            >
              {t('footer.explore', isArabic ? 'استكشف' : 'Explore')}
            </Typography>
            <Stack spacing={1.6}>
              {navLinks.map((link) => (
                <Typography key={link.key} component={Link} to={routeMap[link.key] || '/'} sx={linkHoverSx}>
                  {link.label}
                </Typography>
              ))}
            </Stack>
          </Box>

          {/* عمود بيانات التواصل */}
          <Box sx={{ minWidth: 220 }}>
            <Typography
              sx={{
                fontFamily: bodyFont,
                color: ivory,
                fontSize: '0.85rem',
                fontWeight: 600,
                mb: 2.5,
              }}
            >
              {t('footer.getInTouch', isArabic ? 'تواصل معنا' : 'Get in Touch')}
            </Typography>
            <Stack spacing={1.6}>
              <Typography sx={{ fontFamily: bodyFont, color: ivoryMuted, fontSize: '0.92rem', lineHeight: 1.8 }}>
                {/* {t('contactUs.address', isArabic ? 'كونراد دبي – شارع الشيخ زايد – المركز التجاري الأول – دبي' : 'Conrad Dubai, Sheikh Zayed Rd - Trade Center First - Dubai')} */}
             {t('contactUs.address', isArabic ? 'بناية وايت سوان، شارع الشيخ زايد، الطابق الأول، دبي' : 'WHITE SWAN BUILDING, Sheikh Zayed Road, First Floor, Dubai')}
              </Typography>
              <Typography
                component="a"
                href="tel:+971 58 553 5444"
                sx={{ ...linkHoverSx, direction: 'ltr', textAlign: isArabic ? 'right' : 'left' }}
              >
               +971 58 553 5444
              </Typography>
              <Typography component="a" href="mailto:info@lyorauraproperties.com" sx={linkHoverSx}>
                info@lyorauraproperties.com
              </Typography>
            </Stack>
          </Box>
        </Stack>

        {/* الخط الفاصل قبل السطر السفلي */}
        <Box sx={{ height: '1px', bgcolor: hairline, my: { xs: 5, md: 6 } }} />

        {/* السطر السفلي - الحقوق وروابط قانونية */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 0 }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ pb: { xs: 5, md: 6 } }}
        >
          <Typography sx={{ fontFamily: bodyFont, color: ivoryMuted, fontSize: '0.8rem' }}>
            {t('footer.rights', { year: new Date().getFullYear() })}
          </Typography>

          <Stack direction="row" spacing={3}>
            {legalLinks.map((link) => (
              <Typography key={link.key} component={Link} to={link.to} sx={{ ...linkHoverSx, fontSize: '0.8rem' }}>
                {link.label}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Box>
  )
}